import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { User, Building2 } from "lucide-react";
import { useAuth, formatError } from "@/context/AuthContext";
import { LogoFull } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput, scorePassword } from "@/components/PasswordInput";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const island = sp.get("island");
  const [role, setRole] = useState(sp.get("role") === "loueur" ? "loueur" : "client");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "", phone: "", kbis: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (sp.get("role") === "loueur") setRole("loueur"); }, [sp]);

  const submit = async (e) => {
    e.preventDefault();
    if (scorePassword(form.password) < 3) { toast.error(t("pwdWeak")); return; }
    setLoading(true);
    try {
      const u = await register({ ...form, role, referral_code: sp.get("ref") || null, preferred_island: island || null });
      toast.success("Compte créé !");
      nav(u.role === "loueur" ? "/loueur/onboarding" : "/my/bookings");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="section-sand min-h-[80vh] flex items-center justify-center py-16 px-4">
      <div className="bg-white rounded-3xl shadow-card border border-border p-8 sm:p-10 max-w-md w-full">
        <div className="flex justify-center mb-6"><LogoFull /></div>
        <h1 className="font-heading font-extrabold text-2xl text-ink text-center">{t("auth.register")}</h1>

        <div className="grid grid-cols-2 gap-2 mt-6 bg-sand rounded-xl p-1">
          <button data-testid="role-client-tab" onClick={() => setRole("client")} className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${role === "client" ? "bg-white text-canard shadow-sm" : "text-muted2"}`}>
            <User className="w-4 h-4" /> {t("auth.asClient")}
          </button>
          <button data-testid="role-loueur-tab" onClick={() => setRole("loueur")} className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${role === "loueur" ? "bg-white text-canard shadow-sm" : "text-muted2"}`}>
            <Building2 className="w-4 h-4" /> {t("auth.asLoueur")}
          </button>
        </div>

        {role === "loueur" && island && (
          <p data-testid="register-island-hint" className="mt-3 text-sm text-center text-canard font-body bg-canard/5 rounded-xl py-2 px-3">{t("auth.islandHint", { island })}</p>
        )}

        <form onSubmit={submit} className="mt-5 space-y-3">
          <Input data-testid="register-name" required placeholder={t("auth.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <Input data-testid="register-email" required type="email" placeholder={t("auth.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl bg-sand" />
          <PasswordInput data-testid="register-password" required showStrength placeholder={t("auth.password")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11 rounded-xl bg-sand" />
          {role === "loueur" && (
            <>
              <Input data-testid="register-company" required placeholder={t("auth.company")} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="h-11 rounded-xl bg-sand" />
              <Input data-testid="register-phone" placeholder={t("auth.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-xl bg-sand" />
              <Input data-testid="register-kbis" placeholder={t("auth.kbis")} value={form.kbis} onChange={(e) => setForm({ ...form, kbis: e.target.value })} className="h-11 rounded-xl bg-sand" />
            </>
          )}
          <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full h-12 rounded-full bg-emeraude hover:bg-emeraude-dark font-bold shadow-cta">{loading ? "..." : t("auth.signup")}</Button>
        </form>
        <p className="text-center text-sm text-muted2 font-body mt-6">{t("auth.hasAccount")} <Link to="/login" data-testid="go-login-link" className="text-emeraude font-bold hover:underline">{t("auth.login")}</Link></p>
      </div>
    </div>
  );
}
